import _ from "lodash"
import React, { Fragment, useEffect, useMemo } from "react"

import { AirportSeasonConfigurations } from "../../api/AirportSeasonConfiguration"
import { Flight } from "../../api/Flight"

import { ValueLabel } from "../generic/fields"
import SeasonDateRange from "../SeasonDateRange"

import { getFlightSelectorConfiguration } from "../Utils"

import {
  AircraftRegistrationField,
  AircraftTypeField,
  DOOPField,
  DropdownBooleanField,
  HandlerField,
  MinGroundTimeFieldN,
  MtowField,
  NoiseCertificateField,
  OperatorDropdownField,
  OperatorField,
  OpTextField,
  OvernightIndicatorFieldN,
  ParkingLocationField,
  SeatsField,
  ServiceNumberField,
  ServiceTypeDropdownField,
  StationField,
  TerminalDropdownField,
  TimeField,
  WOOPFieldN
} from "../selectorFields"
import {
  ActionCode,
  EditMode,
  fieldOrder,
  FlightMode,
  GCRMode,
  PageGroup
} from "../../common/transaction"
import DropdownField from "../generic/form/DropdownField"
import { CommonText, FieldName, FlightText } from "../../common/text"

import TimeRangeField from "../generic/form/TimeRangeField"
import TextField from "../generic/form/TextField"
import { CodeType } from "../AirportSelector"
import { showFieldSection } from "./Config"
import SeasonDatePicker from "../SeasonDatePicker"
import Button from "../generic/buttons/Button"
import { useAppSelector } from "../../app/hooks"
import { getTimeAndCodeFilteredAirportSeasons } from "../../components/Utils"
import RadioBooleanField from "../generic/form/RadioBooleanField"
import SeasonDepartureDatePicker from "../SeasonDepartureDatePicker"
import { ErrorText } from "../../common/errorText"
import { CustomShowLabelConfig } from "./TransactionFlightHeaders"

interface Props {
  flight: Flight
  transId: number
  pageGroup: PageGroup
  flightMode: FlightMode
  airports: string[]
  gcrMode: string
  codeType: CodeType
  airportSeasons: AirportSeasonConfigurations
  fieldsWithChanges: string[]
  inFocus?: boolean
  onChange?: (flight: Flight, name: string, value: any, error: boolean) => void
  onFillDown?: (field: FieldName) => void
  touched?: boolean
  customShowLabelConfig: CustomShowLabelConfig
  hasSelectedStartDate?: boolean
  onChangeHasSelectedStartDate?: (val: boolean) => void
  isEditSeries?: boolean
}

const {
  acReg,
  operator,
  serviceNo,
  reason,
  mgt,
  o,
  reqTime,
  acType,
  seats,
  st,
  servType,
  searchTimeRange
} = FlightText

const {
  airport,
  operatorArr,
  operatorDep,
  serviceNoArr,
  serviceNoDep,
  serviceTypeArr,
  serviceTypeDep,
  startDate,
  endDate,
  doop,
  woop,
  clearedTimeArr,
  clearedTimeDep,
  requiredDateArr,
  requiredDateDep,
  requiredTimeArr,
  requiredTimeDep,
  reasonCodeArr,
  reasonCodeDep,
  turnaroundDays,
  minGroundTime,
  aircraftType,
  numSeats,
  aircraftRegistration,
  originStation,
  destinationStation,
  lastStation,
  nextStation,
  offerTimeArrTo,
  offerTimeArrFrom,
  offerTimeDepTo,
  offerTimeDepFrom,
  offerTimeArr,
  offerTimeDep,
  origDest,
  lastNext,
  handler,
  parkingLocation,
  vfr,
  towbar,
  operatorText,
  mtow,
  gabaLounge,
  refuelling,
  noiseCertificate,
  departureDate,
  terminalArr,
  terminalDep,
  reqDate
} = FieldName

const TransactionFlightTurnaroundFields: React.FunctionComponent<Props> = ({
  flight,
  transId,
  flightMode,
  pageGroup,
  airports,
  inFocus,
  gcrMode,
  codeType,
  airportSeasons,
  fieldsWithChanges,
  onChange,
  onFillDown,
  touched,
  customShowLabelConfig,
  hasSelectedStartDate,
  onChangeHasSelectedStartDate,
  isEditSeries
}) => {
  const { season, useLocalTime } =
    useAppSelector(
      (state) => state["selectionReducer"].airportSeasonsSelector
    ) || {}
  const { versionInfo, serviceTypes } = useAppSelector(
    (state) => state["appReducer"]
  )
  const { showOptionalFields, useSSIMView } = useAppSelector(
    (state) => state["selectionReducer"].userSettings
  )
  const config = getFlightSelectorConfiguration(
    airportSeasons,
    season,
    [flight.airport],
    codeType
  )

  const isSSIMView = pageGroup === PageGroup.HANDLING ? false : useSSIMView

  const showSection = (fieldName: string) =>
    showFieldSection(
      fieldName,
      EditMode.EDIT,
      pageGroup,
      versionInfo.handlingApplication !== ""
        ? versionInfo.handlingApplication
        : undefined
    )

  const isDateRange = showSection(startDate) && showSection(endDate)
  const actionCode = flight.requestCode
  const isArrivalRow = flightMode === FlightMode.Arrival
  const isDepartureRow = flightMode === FlightMode.Departure
  const isRRow = actionCode === ActionCode.R
  const isArrivalCRow = isArrivalRow && actionCode === ActionCode.C
  const showCaption = isArrivalCRow && inFocus
  const edit = isRRow && inFocus
  const useICAOCodeType = codeType === CodeType.ICAO
  const isREGMode = gcrMode === GCRMode.REG
  const isGaba = pageGroup === PageGroup.GABA
  const isHandling = pageGroup === PageGroup.HANDLING

  const handlingEdit = {
    acReg: !isREGMode,
    acType: false,
    towBar: false,
    serviceNo: !isREGMode,
    operator: !isREGMode && false
  }

  const arrFlightHasAcRegServiceType =
    flight.serviceTypeArr !== "" &&
    config.showACRegForServiceTypes &&
    config.showACRegForServiceTypes?.includes(flight.serviceTypeArr)
  const depFlightHasAcRegServiceType =
    flight.serviceTypeDep !== "" &&
    config.showACRegForServiceTypes &&
    config.showACRegForServiceTypes?.includes(flight.serviceTypeDep)

  const handleFieldChange = (name: string, value: any, error?: boolean) => {
    const fieldName = name
    const updFlight = _.cloneDeep(flight)
    updFlight[fieldName] = value

    if (fieldName === "startDate" && !isDateRange) {
      updFlight.endDate = value
      updFlight.doop = ""
    }
    if (
      name === "turnaroundDays" &&
      showSection(departureDate) &&
      onChangeHasSelectedStartDate
    ) {
      onChangeHasSelectedStartDate(false)
    }

    onChange(updFlight, name, value, error)
  }

  const fieldHasChanges = (fieldName: string): boolean => {
    return fieldsWithChanges.includes(fieldName)
  }

  const updateValuesAfterOptChange = (
    fieldNames: string[],
    opts: ValueLabel[],
    isRequired?: boolean
  ) => {
    const FieldsWithValues = fieldNames.filter(
      (fieldName) => flight[fieldName] !== ""
    )

    if (FieldsWithValues.length) {
      FieldsWithValues.forEach((fieldName) => {
        const valueExists = opts.find((opt) => flight[fieldName] === opt.value)
        if (!valueExists) {
          handleFieldChange(fieldName, "", isRequired)
        }
      })
    }
  }

  const airportOpts = useMemo(() => {
    const useICAOCodeType = codeType === CodeType.ICAO

    const timeAirportSeasons = getTimeAndCodeFilteredAirportSeasons(
      useLocalTime,
      useICAOCodeType,
      airportSeasons
    )

    const timeAirportSeasonsFiltered = timeAirportSeasons.filter(
      (as) =>
        as.season === season &&
        (gcrMode === GCRMode.REG ? as.allowGcrRegMode : true)
    )

    const opts = _.sortBy(
      timeAirportSeasonsFiltered.map((as) => ({
        label: useICAOCodeType
          ? as.airportIcao.toString()
          : as.airport.toString(),
        value: as.airport.toString()
      }))
    )
    return opts
  }, [airportSeasons, season, useLocalTime, codeType, gcrMode])

  const serviceTypeOpts = useMemo(() => {
    let opts: ValueLabel[]
    if (
      config &&
      config.serviceTypes &&
      config.serviceTypes.length > 0 &&
      !(config.serviceTypes.length === 1 && config.serviceTypes[0] === "")
    ) {
      opts = _.map(config.serviceTypes, (o) => {
        const dst = serviceTypes.find((s) => s.code === o)
        return { value: o, label: dst ? dst.code + " " + dst.description : o }
      })
    } else {
      opts = _.map(serviceTypes, (o) => {
        return { value: o.code, label: o.code + " " + o.description }
      })
    }
    return opts
  }, [config, serviceTypes])

  useEffect(() => {
    if (
      (showSection(serviceTypeArr) || showSection(serviceTypeDep)) &&
      isRRow
    ) {
      updateValuesAfterOptChange(
        ["serviceTypeArr", "serviceTypeDep"],
        serviceTypeOpts,
        true
      )
    }
    // eslint-disable-next-line
  }, [serviceTypeOpts])

  const terminalOpts = useMemo(() => {
    const opts = config?.terminals.map((t) => ({
      label: t,
      value: t
    }))
    return opts
  }, [config])

  const parkingLocationOpts = useMemo(() => {
    const opts =
      config.parkingLocations.length > 1
        ? config.parkingLocations.map((parkingLocation) => ({
            value: parkingLocation,
            label: parkingLocation
          }))
        : []
    return opts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config])

  useEffect(() => {
    const fieldsWithValues = ["terminalArr", "terminalDep"].filter(
      (fieldName) => flight[fieldName] !== null && flight[fieldName] !== ""
    )
    if (
      fieldsWithValues.length &&
      customShowLabelConfig.terminal.showLabelSection &&
      config.supportsTerminalRequest &&
      isRRow
    ) {
      updateValuesAfterOptChange(["terminalArr", "terminalDep"], terminalOpts)
    }
    // eslint-disable-next-line
  }, [terminalOpts])

  useEffect(() => {
    if (
      !showSection(aircraftRegistration) &&
      flight.aircraftRegistration !== "" &&
      !(arrFlightHasAcRegServiceType || depFlightHasAcRegServiceType)
    ) {
      handleFieldChange(FieldName.aircraftRegistration, "", false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flight.serviceTypeArr, flight.serviceTypeDep, flight.airport])

  const getFillDownBtn = (fieldName: FieldName) => {
    return (
      edit &&
      (isHandling ? handlingEdit.acType : true) && (
        <div className="d-flex justify-content-center">
          <Button
            onClick={(e) => {
              e.preventDefault()
              onFillDown(fieldName)
            }}
            title="Fill Down"
            className="fill-down-btn"
          >
            <span className="fill-down-icon" />
          </Button>
        </div>
      )
    )
  }

  const getOperator = (name: string, flight: Flight, caption: string) => {
    const isOperatorRestricted = config && !config.operators.includes("")
    const opts = isOperatorRestricted
      ? _.map(config.operators, (o) => {
          return { value: o, label: o }
        })
      : []
    const isEditable = flight.requestCode === ActionCode.R && inFocus

    return (
      <div className="trans-field-w-operator ocs-mr-12">
        {!isREGMode &&
          (isOperatorRestricted ? (
            <OperatorDropdownField
              name={name}
              value={flight[name]}
              options={opts}
              caption={caption}
              readonly={
                !(isEditable && (isHandling ? handlingEdit.operator : true))
              }
              required
              touched={touched}
              onChange={handleFieldChange}
              hasChanges={fieldHasChanges(name)}
              menuPortalTarget={document.body}
            />
          ) : (
            <OperatorField
              required
              touched={touched}
              name={name}
              value={flight[name]}
              caption={caption}
              readonly={
                !(isEditable && (isHandling ? handlingEdit.operator : true))
              }
              hasChanges={fieldHasChanges(name)}
              onChange={handleFieldChange}
            />
          ))}
      </div>
    )
  }

  const getServiceType = (name: FieldName, flight: Flight, caption: string) => {
    const isEditable = flight.requestCode === ActionCode.R && inFocus
    return (
      <div className="trans-field-w-service-type ocs-mr-12">
        <ServiceTypeDropdownField
          name={name}
          required
          isEmpty={
            flight.requestCode !== ActionCode.R ? false : flight[name] === ""
          }
          value={flight[name]}
          touched={touched}
          caption={caption}
          options={serviceTypeOpts}
          readonly={!isEditable}
          hasChanges={fieldHasChanges(name)}
          onChange={handleFieldChange}
          formatOptionLabel={(opt, { context }) => {
            return context === "menu" ? opt.label : opt.value
          }}
          menuPortalTarget={document.body}
        />
        {getFillDownBtn(name)}
      </div>
    )
  }

  const getACRegField = (key: number) => {
    const showOptional = isGaba || isHandling ? true : showOptionalFields
    return (
      showOptional &&
      (customShowLabelConfig.serviceTypeAcReg.showLabelSection ||
        showSection(aircraftRegistration)) && (
        <div key={key} className="trans-field-w-ac-reg ocs-mr-12">
          {isArrivalRow &&
            (customShowLabelConfig.serviceTypeAcReg.showLabelSection
              ? arrFlightHasAcRegServiceType || depFlightHasAcRegServiceType
              : true) && (
              <AircraftRegistrationField
                value={flight.aircraftRegistration}
                caption={showCaption ? acReg : null}
                readonly={!(edit && (isHandling ? handlingEdit.acReg : true))}
                hasChanges={fieldHasChanges(aircraftRegistration)}
                onChange={handleFieldChange}
                absoluteOptional
                touched={touched}
              />
            )}
        </div>
      )
    )
  }

  const getAirportField = (key: number) => {
    return (
      showSection(airport) && (
        <div
          key={key}
          className={
            "ocs-mr-12 " +
            (useICAOCodeType
              ? "trans-field-w-airport-readonly-icao"
              : "trans-field-w-airport-readonly")
          }
        >
          <DropdownField
            name={airport}
            required
            touched={touched}
            caption={showCaption ? FlightText.airport : null}
            clearable={false}
            closeOnSelect
            readonly
            options={airportOpts}
            labelOnReadOnly={useICAOCodeType}
            value={flight.airport}
            onChange={handleFieldChange}
            menuPortalTarget={document.body}
          />
        </div>
      )
    )
  }

  const getDateFields = (key: number) => {
    return (
      <div
        key={key}
        className={
          isDateRange
            ? "trans-field-w-date-range ocs-mr-4"
            : "ocs-mr-12 trans-field-w-date"
        }
      >
        {isArrivalRow &&
          (isDateRange ? (
            <SeasonDateRange
              required
              dateRangeStartValue={flight.startDate}
              dateRangeEndValue={flight.endDate}
              showCaption={showCaption}
              readonly={!inFocus}
              onChange={handleFieldChange}
              showIcon={false}
              hasChanges={
                fieldHasChanges(startDate) || fieldHasChanges(endDate)
              }
              dropdownId={"transItem-" + transId}
              autoFocus={isArrivalCRow}
              touched={touched}
            />
          ) : (
            <SeasonDatePicker
              name={startDate}
              required
              touched={touched}
              value={flight.startDate}
              caption={showCaption ? "Date" : null}
              onChange={handleFieldChange}
              dropdownId={"transItem-" + transId}
              hasChanges={fieldHasChanges(startDate)}
              readonly={!edit}
              autoFocus={isArrivalCRow}
              onSelectDate={() => onChangeHasSelectedStartDate(true)}
              // placement={placement}
            />
          ))}
        {isDepartureRow && showSection(departureDate) && (
          <SeasonDepartureDatePicker
            turnaroundDays={flight.turnaroundDays}
            startDate={flight.startDate}
            onChange={(name, value, isValid) => {
              handleFieldChange(name, value, isValid)
            }}
            hasChanges={fieldHasChanges(turnaroundDays)}
            readonly={!edit}
            hasSelectedStartDate={hasSelectedStartDate}
          />
        )}
      </div>
    )
  }

  const getReqDate = (key: number) => {
    return (
      (showSection(requiredDateArr) || showSection(requiredDateArr)) && (
        <div key={key} className="trans-field-w-req-date ocs-mr-12">
          {isArrivalRow && (
            <SeasonDatePicker
              name={requiredDateArr}
              value={flight.requiredDateArr}
              caption={showCaption ? FlightText.reqDate : null}
              onChange={handleFieldChange}
              hasChanges={fieldHasChanges(requiredDateArr)}
              readonly={!edit}
              touched={touched}
              required={
                isRRow &&
                flight.requiredTimeArr === "" &&
                flight.requiredDateArr !== null
              }
              dateCanBeNull
              dateHasErrText={ErrorText.reqDateError}
            />
          )}
          {isDepartureRow && (
            <SeasonDatePicker
              name={requiredDateDep}
              value={flight.requiredDateDep}
              caption={showCaption ? FlightText.reqDate : null}
              onChange={handleFieldChange}
              hasChanges={fieldHasChanges(requiredDateDep)}
              readonly={!edit}
              touched={touched}
              dateCanBeNull
              required={
                isRRow &&
                flight.requiredTimeDep === "" &&
                flight.requiredDateDep !== null
              }
              dateHasErrText={ErrorText.reqDateError}
            />
          )}
        </div>
      )
    )
  }

  const getDoopField = (key: number) => {
    return (
      showSection(doop) && (
        <div key={key} className="trans-field-w-doop ocs-mr-12">
          {isArrivalRow && (
            <DOOPField
              value={flight.doop}
              caption={showCaption ? FlightText.doop : null}
              readonly={!inFocus}
              hasChanges={fieldHasChanges(doop)}
              onChange={handleFieldChange}
            />
          )}
        </div>
      )
    )
  }

  const getWoopField = (key: number) => {
    return (
      showOptionalFields &&
      showSection(woop) && (
        <div key={key} className="trans-field-w-woop ocs-mr-12">
          {isArrivalRow && (
            <WOOPFieldN
              name={woop}
              caption={showCaption ? "F" : null}
              value={flight.woop}
              readonly={!edit}
              onChange={handleFieldChange}
              absoluteOptional
              touched={touched}
              isEmpty={flight.woop === null}
              required={isEditSeries}
            />
          )}
        </div>
      )
    )
  }

  const getClearedTimeFields = (key: number) => {
    return (
      <div key={key}>
        {isArrivalRow && showSection(clearedTimeArr) && (
          <div className="trans-field-w-cleared-time ocs-mr-12">
            <TimeField
              name={clearedTimeArr}
              allow9999time
              required
              touched={touched}
              caption={showCaption ? "ClearTime" : null}
              readonly={!edit}
              hasChanges={fieldHasChanges(clearedTimeArr)}
              value={flight.clearedTimeArr}
              onChange={handleFieldChange}
            />
          </div>
        )}
        {isDepartureRow && showSection(clearedTimeDep) && (
          <div className="trans-field-w-cleared-time ocs-mr-12">
            <TimeField
              name={clearedTimeDep}
              allow9999time
              required
              touched={touched}
              caption={null}
              value={flight.clearedTimeDep}
              hasChanges={fieldHasChanges(clearedTimeDep)}
              readonly={!edit}
              onChange={handleFieldChange}
            />
          </div>
        )}
      </div>
    )
  }

  const getReqTimeFields = (key: number) => {
    return (
      <div key={key}>
        {isArrivalRow && (
          <div className="trans-field-w-req-time ocs-mr-12">
            <TimeField
              name={requiredTimeArr}
              caption={showCaption ? reqTime : null}
              value={flight.requiredTimeArr}
              readonly={!edit}
              hasChanges={fieldHasChanges(requiredTimeArr)}
              onChange={handleFieldChange}
              touched={touched}
            />
          </div>
        )}
        {isDepartureRow && (
          <div className={"trans-field-w-req-time ocs-mr-12"}>
            <TimeField
              name={requiredTimeDep}
              caption={null}
              value={flight.requiredTimeDep}
              readonly={!edit}
              hasChanges={fieldHasChanges(requiredTimeDep)}
              onChange={handleFieldChange}
              touched={touched}
            />
          </div>
        )}
      </div>
    )
  }

  const getReasonCodeFields = (key: number) => {
    return (
      showSection(reasonCodeArr) && (
        <div key={key}>
          {isArrivalRow && (
            <div className="trans-field-w-reason-code ocs-mr-12">
              <TextField
                name={reasonCodeArr}
                caption={showCaption ? reason : null}
                value={flight.reasonCodeArr}
                readonly
                uppercase
              />
            </div>
          )}
          {isDepartureRow && (
            <div className={"trans-field-w-reason-code ocs-mr-12"}>
              <TextField
                name={reasonCodeDep}
                caption={null}
                value={flight.reasonCodeDep}
                readonly
                uppercase
              />
            </div>
          )}
        </div>
      )
    )
  }

  const getTurnaroundDaysField = (key: number) => {
    return (
      showOptionalFields &&
      showSection(turnaroundDays) && (
        <div key={key} className="trans-field-w-turnaround-days ocs-mr-12">
          {(isArrivalCRow || isDepartureRow) && (
            <OvernightIndicatorFieldN
              caption={showCaption ? o : null}
              name={turnaroundDays}
              value={isDepartureRow ? flight.turnaroundDays : null}
              readonly={!edit}
              hasChanges={fieldHasChanges(turnaroundDays)}
              onChange={handleFieldChange}
            />
          )}
        </div>
      )
    )
  }

  const getMinGroundTimeField = (key: number) => {
    return (
      showOptionalFields && (
        <div key={key} className="trans-field-w-mgt ocs-mr-12">
          {isArrivalRow && (
            <MinGroundTimeFieldN
              caption={showCaption ? mgt : null}
              value={flight.minGroundTime}
              readonly={!edit}
              hasChanges={fieldHasChanges(minGroundTime)}
              onChange={handleFieldChange}
              absoluteOptional
              required
              isEmpty={flight.minGroundTime === null}
              touched={touched}
            />
          )}
        </div>
      )
    )
  }

  const getAircraftTypeField = (key: number) => {
    return (
      <div key={key} className="trans-field-w-ac-type ocs-mr-12">
        {isArrivalRow && (
          <Fragment>
            <AircraftTypeField
              required
              touched={touched}
              caption={showCaption ? acType : null}
              value={flight.aircraftType}
              readonly={!(edit && (isHandling ? handlingEdit.acType : true))}
              codeType={codeType}
              hasChanges={fieldHasChanges(aircraftType)}
              onChange={handleFieldChange}
            />
            {getFillDownBtn(aircraftType)}
          </Fragment>
        )}
      </div>
    )
  }

  const getNumSeatsField = (key: number) => {
    return (
      <div key={key} className="trans-field-w-seats ocs-mr-12">
        {isArrivalRow && (
          <Fragment>
            <SeatsField
              required
              touched={touched}
              caption={showCaption ? seats : null}
              name={numSeats}
              value={flight.numSeats}
              readonly={!edit}
              hasChanges={fieldHasChanges(numSeats)}
              onChange={handleFieldChange}
            />
            {getFillDownBtn(numSeats)}
          </Fragment>
        )}
      </div>
    )
  }

  const getOriginField = (key: number) => {
    return (
      showSection(originStation) && (
        <div
          key={key}
          className={
            "ocs-mr-12 " +
            (useICAOCodeType
              ? "trans-field-w-station-icao"
              : "trans-field-w-station")
          }
        >
          {isArrivalRow && (
            <>
              <StationField
                required
                touched={touched}
                name={originStation}
                caption={showCaption ? FlightText.origin : null}
                value={flight.originStation}
                codeType={codeType}
                readonly={!edit}
                hasChanges={fieldHasChanges(originStation)}
                onChange={handleFieldChange}
              />
              {getFillDownBtn(originStation)}
            </>
          )}
        </div>
      )
    )
  }

  const getDestField = (key: number) => {
    return (
      showSection(destinationStation) && (
        <div
          key={key}
          className={
            "ocs-mr-12 " +
            (useICAOCodeType
              ? "trans-field-w-station-icao"
              : "trans-field-w-station")
          }
        >
          {isDepartureRow && (
            <>
              <StationField
                required
                touched={touched}
                name={destinationStation}
                caption={showCaption ? FlightText.dest : null}
                value={flight.destinationStation}
                codeType={codeType}
                readonly={!edit}
                hasChanges={fieldHasChanges(destinationStation)}
                onChange={handleFieldChange}
              />
              {getFillDownBtn(destinationStation)}
            </>
          )}
        </div>
      )
    )
  }

  const getLastField = (key: number) => {
    return (
      showOptionalFields &&
      showSection(lastStation) && (
        <div
          key={key}
          className={
            "ocs-mr-12 " +
            (useICAOCodeType
              ? "trans-field-w-station-icao"
              : "trans-field-w-station")
          }
        >
          {isArrivalRow && (
            <>
              <StationField
                name={lastStation}
                caption={showCaption ? CommonText.last : null}
                value={flight.lastStation}
                codeType={codeType}
                readonly={!edit}
                hasChanges={fieldHasChanges(lastStation)}
                onChange={handleFieldChange}
                touched={touched}
              />
              {getFillDownBtn(lastStation)}
            </>
          )}
        </div>
      )
    )
  }

  const getNextField = (key: number) => {
    return (
      showOptionalFields &&
      showSection(nextStation) && (
        <div
          key={key}
          className={
            "ocs-mr-12 " +
            (useICAOCodeType
              ? "trans-field-w-station-icao"
              : "trans-field-w-station")
          }
        >
          {isDepartureRow && (
            <>
              <StationField
                name={nextStation}
                caption={showCaption ? CommonText.next : null}
                value={flight.nextStation}
                codeType={codeType}
                readonly={!edit}
                hasChanges={fieldHasChanges(nextStation)}
                onChange={handleFieldChange}
                touched={touched}
              />
              {getFillDownBtn(nextStation)}
            </>
          )}
        </div>
      )
    )
  }

  const getOrigDestFields = (key: number) => {
    return (
      <div key={key}>
        {isArrivalRow && showSection(originStation) && (
          <div
            className={
              "ocs-mr-12 " +
              (useICAOCodeType
                ? "trans-field-w-station-icao"
                : "trans-field-w-station")
            }
          >
            <StationField
              required
              touched={touched}
              name={originStation}
              caption={showCaption ? FlightText.origDest : null}
              value={flight.originStation}
              codeType={codeType}
              readonly={!edit}
              hasChanges={fieldHasChanges(originStation)}
              onChange={handleFieldChange}
            />
            {getFillDownBtn(originStation)}
          </div>
        )}
        {isDepartureRow && showSection(destinationStation) && (
          <div
            className={
              "ocs-mr-12 " +
              (useICAOCodeType
                ? "trans-field-w-station-icao"
                : "trans-field-w-station")
            }
          >
            <StationField
              required
              touched={touched}
              name={destinationStation}
              caption={null}
              value={flight.destinationStation}
              codeType={codeType}
              readonly={!edit}
              hasChanges={fieldHasChanges(destinationStation)}
              onChange={handleFieldChange}
            />
            {getFillDownBtn(destinationStation)}
          </div>
        )}
      </div>
    )
  }

  const getLastNextFields = (key: number) => {
    return (
      <div key={key}>
        {isArrivalRow && showOptionalFields && showSection(lastStation) && (
          <div
            className={
              "ocs-mr-12 " +
              (useICAOCodeType
                ? "trans-field-w-station-icao"
                : "trans-field-w-station")
            }
          >
            <StationField
              name={lastStation}
              caption={showCaption ? CommonText.lastNext : null}
              value={flight.lastStation}
              codeType={codeType}
              readonly={!edit}
              hasChanges={fieldHasChanges(lastStation)}
              onChange={handleFieldChange}
              touched={touched}
            />
            {getFillDownBtn(lastStation)}
          </div>
        )}
        {isDepartureRow && showOptionalFields && showSection(nextStation) && (
          <div
            className={
              "ocs-mr-12 " +
              (useICAOCodeType
                ? "trans-field-w-station-icao"
                : "trans-field-w-station")
            }
          >
            <StationField
              name={nextStation}
              caption={null}
              value={flight.nextStation}
              codeType={codeType}
              readonly={!edit}
              hasChanges={fieldHasChanges(nextStation)}
              onChange={handleFieldChange}
              touched={touched}
            />
            {getFillDownBtn(nextStation)}
          </div>
        )}
      </div>
    )
  }

  const getOfferTimeFields = (key: number) => {
    return (
      showOptionalFields &&
      (showSection(offerTimeArr) || showSection(offerTimeDep)) && (
        <section key={key} className="ocs-mr-4">
          {isArrivalRow && (
            <section className="trans-field-w-search-time-range">
              <TimeRangeField
                caption={!showCaption ? null : searchTimeRange}
                fromName={offerTimeArrFrom}
                fromValue={flight.offerTimeArrFrom}
                toName={offerTimeArrTo}
                toValue={flight.offerTimeArrTo}
                onChange={handleFieldChange}
                touched={touched}
                readonly={!edit}
                hasChanges={
                  fieldHasChanges(offerTimeArrTo) ||
                  fieldHasChanges(offerTimeArrFrom)
                }
              />
            </section>
          )}
          {isDepartureRow && (
            <section className="trans-field-w-search-time-range">
              <TimeRangeField
                caption={null}
                fromName={offerTimeDepFrom}
                fromValue={flight.offerTimeDepFrom}
                toName={offerTimeDepTo}
                toValue={flight.offerTimeDepTo}
                onChange={handleFieldChange}
                touched={touched}
                readonly={!edit}
                hasChanges={
                  fieldHasChanges(offerTimeDepTo) ||
                  fieldHasChanges(offerTimeDepFrom)
                }
              />
            </section>
          )}
        </section>
      )
    )
  }

  const getOperatorFields = (key: number) => {
    return (
      <div key={key}>
        {isArrivalRow &&
          showSection(operatorArr) &&
          getOperator(operatorArr, flight, showCaption ? operator : null)}

        {isDepartureRow &&
          showSection(operatorDep) &&
          getOperator(operatorDep, flight, null)}
      </div>
    )
  }

  const getServiceNoFields = (key: number) => {
    return (
      <div key={key}>
        {isArrivalRow && showSection(serviceNoArr) && (
          <div className="trans-field-w-service-no ocs-mr-12">
            {!isREGMode && (
              <ServiceNumberField
                name={serviceNoArr}
                required
                touched={touched}
                value={flight.serviceNoArr}
                caption={showCaption ? serviceNo : null}
                readonly={!edit}
                hasChanges={fieldHasChanges(serviceNoArr)}
                onChange={handleFieldChange}
              />
            )}
          </div>
        )}
        {isDepartureRow && showSection(serviceNoDep) && (
          <div className="trans-field-w-service-no ocs-mr-12">
            {!isREGMode && (
              <ServiceNumberField
                name={serviceNoDep}
                required
                touched={touched}
                caption={null}
                value={flight.serviceNoDep}
                readonly={!edit}
                hasChanges={fieldHasChanges(serviceNoDep)}
                onChange={handleFieldChange}
              />
            )}
          </div>
        )}
      </div>
    )
  }

  const getServiceTypeFields = (key: number) => {
    return (
      <div key={key}>
        {isArrivalRow &&
          getServiceType(
            serviceTypeArr,
            flight,
            showCaption
              ? pageGroup === PageGroup.HANDLING
                ? servType
                : st
              : null
          )}
        {isDepartureRow && getServiceType(serviceTypeDep, flight, null)}
      </div>
    )
  }

  const getMTOWField = (key: number) => {
    return (
      showSection(mtow) && (
        <div key={key} className="trans-field-w-mtow ocs-mr-12">
          {isArrivalRow && (
            <Fragment>
              <MtowField
                value={flight.mtow}
                onChange={handleFieldChange}
                caption={!showCaption ? null : FlightText.mtow}
                hasChanges={fieldHasChanges(mtow)}
                readonly={!edit}
                required
                isEmpty={flight.mtow === null}
                touched={touched}
              />

              {/* {getFillDownBtn(numSeats)} */}
            </Fragment>
          )}
        </div>
      )
    )
  }

  const getOperatorTextField = (key: number) => {
    return (
      showSection(operatorText) && (
        <div key={key} className="trans-field-w-operator-text ocs-mr-12">
          {isArrivalRow && (
            <Fragment>
              <OpTextField
                value={flight.operatorText}
                required
                isEmpty={flight.operatorText === ""}
                onChange={handleFieldChange}
                caption={!showCaption ? null : FlightText.operatorTextCaption}
                hasChanges={fieldHasChanges(operatorText)}
                readonly={!edit}
                touched={touched}
              />
              {/* {getFillDownBtn(numSeats)} */}
            </Fragment>
          )}
        </div>
      )
    )
  }

  const getHandlerField = (key: number) => {
    const handlerOpts = airportSeasons[0].handlers.map((handler) => ({
      value: handler,
      label: handler
    }))

    return (
      showSection(handler) && (
        <div key={key} className="trans-field-w-handler ocs-mr-12">
          {isArrivalRow && (
            <Fragment>
              <HandlerField
                value={flight.handler}
                onChange={handleFieldChange}
                caption={!showCaption ? null : FlightText.handler}
                hasChanges={fieldHasChanges(handler)}
                readonly={!edit}
                options={handlerOpts}
                required
                isEmpty={!isRRow ? false : flight.handler === ""}
                touched={touched}
              />
            </Fragment>
          )}
        </div>
      )
    )
  }

  const getParkingLocationField = (key: number) => {
    return (
      showSection(parkingLocation) &&
      customShowLabelConfig.parkingLocation.showLabelSection && (
        <div key={key} className="trans-field-w-parking-loc ocs-mr-12">
          {isArrivalRow && parkingLocationOpts.length > 0 && (
            <Fragment>
              <ParkingLocationField
                value={flight.parkingLocation}
                options={parkingLocationOpts}
                onChange={handleFieldChange}
                caption={!showCaption ? null : FlightText.parkLoc}
                readonly={!edit}
                hasChanges={fieldHasChanges(parkingLocation)}
                required
                isEmpty={!isRRow ? false : flight.parkingLocation === ""}
                touched={touched}
                menuPortalTarget={document.body}
              />
            </Fragment>
          )}
        </div>
      )
    )
  }

  const getNoiseField = (key: number) => {
    return (
      showSection(noiseCertificate) && (
        <div key={key} className="trans-field-w-noise ocs-mr-12">
          {isArrivalRow && (
            <Fragment>
              <NoiseCertificateField
                value={flight.noiseCertificate}
                caption={!showCaption ? null : FlightText.noise}
                onChange={handleFieldChange}
                readonly={!edit}
                hasChanges={fieldHasChanges(noiseCertificate)}
              />
            </Fragment>
          )}
        </div>
      )
    )
  }

  const getVfrField = (key: number) => {
    return (
      showSection(vfr) && (
        <div key={key} className="trans-field-w-flight-rules ocs-mr-12">
          {isArrivalRow && (
            <Fragment>
              <DropdownBooleanField
                caption={!showCaption ? null : FlightText.vfrCaption}
                name={FieldName.vfr}
                booleanValue={flight.vfr}
                booleanOptions={[
                  { label: FlightText.vfr, value: true },
                  { label: FlightText.ifr, value: false }
                ]}
                onChangeBooleanField={handleFieldChange}
                readonly={!edit}
                hasChanges={fieldHasChanges(vfr)}
              />
            </Fragment>
          )}
        </div>
      )
    )
  }

  const getTowbarField = (key: number) => {
    return (
      showSection(towbar) && (
        <div key={key} className="trans-field-w-towbar ocs-mr-12">
          {isArrivalRow && (
            <Fragment>
              <DropdownBooleanField
                caption={!showCaption ? null : "Towbar"}
                name={FieldName.towbar}
                booleanValue={flight.towbar}
                booleanOptions={[
                  { label: "Yes", value: true },
                  { label: "No", value: false }
                ]}
                onChangeBooleanField={handleFieldChange}
                readonly={!(edit && (isHandling ? handlingEdit.towBar : true))}
                hasChanges={fieldHasChanges(towbar)}
              />
            </Fragment>
          )}
        </div>
      )
    )
  }

  const getLoungeField = (key: number) => {
    return (
      showOptionalFields &&
      showSection(gabaLounge) && (
        <div key={key} className="trans-field-w-lounge ocs-mr-12">
          {isArrivalRow && (
            <Fragment>
              <RadioBooleanField
                caption={!showCaption ? null : FlightText.gabaLounge}
                name={FieldName.gabaLounge}
                value={flight.gabaLounge}
                options={[
                  { label: "Yes", value: true },
                  { label: "No", value: false }
                ]}
                onChange={handleFieldChange}
                readonly={!edit}
                hasChanges={fieldHasChanges(gabaLounge)}
                className="h-auto"
              />
            </Fragment>
          )}
        </div>
      )
    )
  }

  const getRefuellingField = (key: number) => {
    return (
      showOptionalFields &&
      showSection(refuelling) && (
        <div key={key} className="trans-field-w-refuel ocs-mr-12">
          {isArrivalRow && (
            <Fragment>
              <RadioBooleanField
                caption={!showCaption ? null : FlightText.refuelling}
                name={FieldName.refuelling}
                value={flight.refuelling}
                options={[
                  { label: "Yes", value: true },
                  { label: "No", value: false }
                ]}
                onChange={handleFieldChange}
                readonly={!edit}
                hasChanges={fieldHasChanges(refuelling)}
                className="h-auto"
              />
            </Fragment>
          )}
        </div>
      )
    )
  }

  const getTerminalFields = (key: number) => {
    return (
      customShowLabelConfig.terminal.showLabelSection && (
        <section key={key} className="ocs-field-item trans-field-w-terminal">
          {isArrivalRow &&
            config.supportsTerminalRequest &&
            showSection(terminalArr) && (
              <section className="trans-field-w-terminal ocs-mr-12">
                <TerminalDropdownField
                  name={terminalArr}
                  caption={!showCaption ? null : FlightText.terminal}
                  options={terminalOpts}
                  value={flight.terminalArr}
                  readonly={!edit}
                  onChange={handleFieldChange}
                  hasChanges={fieldHasChanges(terminalArr)}
                  required
                  touched={touched}
                  isEmpty={flight.terminalArr === null}
                />
              </section>
            )}
          {isDepartureRow &&
            config.supportsTerminalRequest &&
            showSection(terminalDep) && (
              <section className={"trans-field-w-terminal ocs-mr-12"}>
                <TerminalDropdownField
                  name={terminalDep}
                  caption={null}
                  options={terminalOpts}
                  value={flight.terminalDep}
                  readonly={!edit}
                  onChange={handleFieldChange}
                  hasChanges={fieldHasChanges(terminalDep)}
                  required
                  touched={touched}
                  isEmpty={flight.terminalDep === null}
                />
              </section>
            )}
        </section>
      )
    )
  }

  const fieldMap = {
    [aircraftRegistration]: getACRegField,
    [minGroundTime]: getMinGroundTimeField,
    [airport]: getAirportField,
    [operatorArr]: getOperatorFields,
    [serviceNoArr]: getServiceNoFields,
    [serviceTypeArr]: getServiceTypeFields,
    [startDate]: getDateFields,
    [reqDate]: getReqDate,
    [doop]: getDoopField,
    [woop]: getWoopField,
    [clearedTimeArr]: getClearedTimeFields,
    [requiredTimeArr]: getReqTimeFields,
    [reasonCodeArr]: getReasonCodeFields,
    [turnaroundDays]: getTurnaroundDaysField,
    [aircraftType]: getAircraftTypeField,
    [numSeats]: getNumSeatsField,
    [origDest]: getOrigDestFields,
    [lastNext]: getLastNextFields,
    [originStation]: getOriginField,
    [lastStation]: getLastField,
    [destinationStation]: getDestField,
    [nextStation]: getNextField,
    [offerTimeArr]: getOfferTimeFields,
    [handler]: getHandlerField,
    [parkingLocation]: getParkingLocationField,
    [noiseCertificate]: getNoiseField,
    [vfr]: getVfrField,
    [towbar]: getTowbarField,
    [mtow]: getMTOWField,
    [operatorText]: getOperatorTextField,
    [gabaLounge]: getLoungeField,
    [refuelling]: getRefuellingField,
    [terminalArr]: getTerminalFields
  }

  const handleGetFields = () => {
    let fields = []
    const orderFormat = isSSIMView ? "SSIMOrder" : "categoryOrder"
    const orderedFields = _.cloneDeep(fieldOrder).sort((a, b) => {
      return (
        ((isGaba || isHandling) && a.gabaOrder ? a.gabaOrder : a[orderFormat]) -
        ((isGaba || isHandling) && b.gabaOrder ? b.gabaOrder : b[orderFormat])
      )
    })

    orderedFields.forEach((f, idx) => {
      if (fieldMap[f.type] && f[orderFormat] >= 0) {
        fields.push(fieldMap[f.type](idx))
      }
    })
    return fields
  }

  return <div className="d-flex ocs-mb-6">{handleGetFields()}</div>
}

export default TransactionFlightTurnaroundFields
